import React, { useRef } from 'react';
import { useEditorContext } from '../context/EditorContext';
import type { TemplateConfig } from '../types';

// ─── Icon atoms ───────────────────────────────────────────────

function IconBtn({
  title, disabled, active, danger, onClick, children,
}: {
  title: string;
  disabled?: boolean;
  active?: boolean;
  danger?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, borderRadius: 6, border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        background: active ? '#1d4ed8' : 'transparent',
        color: disabled ? '#334155' : danger ? '#f87171' : active ? '#fff' : '#94a3b8',
        transition: 'background 0.15s, color 0.15s',
        flexShrink: 0,
      }}
      onMouseEnter={(e) => {
        if (!disabled && !active) (e.currentTarget as HTMLButtonElement).style.background = '#1e293b';
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <div style={{ width: 1, height: 20, background: '#1e293b', flexShrink: 0 }} />;
}

// ─── SVG icons ────────────────────────────────────────────────

const UndoIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 14L4 9l5-5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 9h10.5a5.5 5.5 0 010 11H11" />
  </svg>
);

const RedoIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 14l5-5-5-5" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M20 9H9.5a5.5 5.5 0 000 11H13" />
  </svg>
);

const ZoomInIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <circle cx="11" cy="11" r="8" />
    <path strokeLinecap="round" d="M21 21l-4.35-4.35M11 8v6M8 11h6" />
  </svg>
);

const ZoomOutIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <circle cx="11" cy="11" r="8" />
    <path strokeLinecap="round" d="M21 21l-4.35-4.35M8 11h6" />
  </svg>
);

const FitIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 8V6a2 2 0 012-2h2M4 16v2a2 2 0 002 2h2M16 4h2a2 2 0 012 2v2M16 20h2a2 2 0 002-2v-2" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const PlayIcon = () => (
  <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

// ─── Snap icon ────────────────────────────────────────────────

const SnapIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4h4v4H4zM16 4h4v4h-4zM4 16h4v4H4zM16 16h4v4h-4z" />
    <path strokeLinecap="round" strokeDasharray="3 2" d="M8 6h8M6 8v8M18 8v8M8 18h8" />
  </svg>
);

const SaveIcon = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V7l-4-4z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 21v-8H7v8M7 3v5h8" />
  </svg>
);

// ─── Toolbar ──────────────────────────────────────────────────

export function Toolbar({ onSave }: { onSave?: () => void }) {
  const { state, setMode, setZoom, undo, redo, canUndo, canRedo, updateDuration, loadTemplate, setSnapGrid } = useEditorContext();
  const { mode, zoom, editableConfig, snapGrid } = state;
  const duration = editableConfig?.metadata.duration ?? 30;
  const importRef = useRef<HTMLInputElement>(null);

  const handleExport = () => {
    if (!editableConfig) return;
    const json = JSON.stringify(editableConfig, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${editableConfig.metadata.id}-template.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const config = JSON.parse(ev.target?.result as string) as TemplateConfig;
        loadTemplate(config);
      } catch {
        alert('Invalid template JSON file');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const zoomPct = Math.round(zoom * 100);
  const zoomStep = 0.1;

  return (
    <div
      style={{
        height: 44, flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 4, padding: '0 12px',
        borderBottom: '1px solid #1e293b',
        background: '#0f172a',
        userSelect: 'none',
      }}
    >
      {/* Edit / Preview toggle */}
      <div style={{ display: 'flex', background: '#1e293b', borderRadius: 7, padding: 2, gap: 2 }}>
        <button
          type="button"
          onClick={() => setMode('edit')}
          style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
            borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
            background: mode === 'edit' ? '#1d4ed8' : 'transparent',
            color: mode === 'edit' ? '#fff' : '#64748b',
            transition: 'background 0.15s',
          }}
        >
          <EditIcon /> Edit
        </button>
        <button
          type="button"
          onClick={() => setMode('preview')}
          style={{
            display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px',
            borderRadius: 5, border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600,
            background: mode === 'preview' ? '#059669' : 'transparent',
            color: mode === 'preview' ? '#fff' : '#64748b',
            transition: 'background 0.15s',
          }}
        >
          <PlayIcon /> Preview
        </button>
      </div>

      <Divider />

      {/* Undo / Redo */}
      <IconBtn title="Undo (⌘Z)" disabled={!canUndo} onClick={undo}><UndoIcon /></IconBtn>
      <IconBtn title="Redo (⌘⇧Z)" disabled={!canRedo} onClick={redo}><RedoIcon /></IconBtn>

      <Divider />

      {/* Zoom controls */}
      <IconBtn title="Zoom Out" disabled={zoom <= 0.1} onClick={() => setZoom(zoom - zoomStep)}><ZoomOutIcon /></IconBtn>

      <div style={{ fontSize: 11, color: '#64748b', minWidth: 42, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
        {zoomPct}%
      </div>

      <IconBtn title="Zoom In" disabled={zoom >= 2} onClick={() => setZoom(zoom + zoomStep)}><ZoomInIcon /></IconBtn>
      <IconBtn title="Fit to canvas" onClick={() => setZoom(1)}><FitIcon /></IconBtn>

      <Divider />

      {/* Duration control */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <svg width="13" height="13" fill="none" stroke="#64748b" strokeWidth={2} viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <path strokeLinecap="round" d="M12 6v6l4 2" />
        </svg>
        <input
          type="number"
          value={duration}
          min={1}
          max={600}
          step={1}
          title="Video duration (seconds)"
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!isNaN(v) && v >= 1) updateDuration(v);
          }}
          style={{
            width: 44, background: '#1e293b', border: '1px solid #334155',
            borderRadius: 5, color: '#e2e8f0', fontSize: 11, padding: '3px 5px',
            outline: 'none', textAlign: 'center',
          }}
        />
        <span style={{ fontSize: 10, color: '#64748b' }}>sec</span>
      </div>

      <Divider />

      {/* Snap to grid toggle */}
      <IconBtn
        title={snapGrid ? 'Snap to Grid: ON (click to disable)' : 'Snap to Grid: OFF (click to enable)'}
        active={snapGrid}
        onClick={() => setSnapGrid(!snapGrid)}
      >
        <SnapIcon />
      </IconBtn>

      <Divider />

      {/* Export / Import JSON */}
      <IconBtn title="Export template as JSON" disabled={!editableConfig} onClick={handleExport}>
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
      </IconBtn>
      <IconBtn title="Import template from JSON" onClick={() => importRef.current?.click()}>
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        </svg>
      </IconBtn>
      <input ref={importRef} type="file" accept=".json" onChange={handleImport} style={{ display: 'none' }} />

      <Divider />

      {/* Save to library */}
      <IconBtn title="Save to My Templates" disabled={!editableConfig || !onSave} onClick={() => onSave?.()}>
        <SaveIcon />
      </IconBtn>

      {/* Mode badge on the right */}
      <div style={{ marginLeft: 'auto', fontSize: 10, color: '#475569', fontFamily: 'monospace' }}>
        {mode === 'edit' ? 'Edit Mode' : 'Preview Mode'}
      </div>
    </div>
  );
}
